import { useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, MessageSquare, Phone, UserPlus } from 'lucide-react';
import { SchnellErfassen, type SchnellErfassenOption } from '@elbklar/ui';
import { contactsApi, customersApi, partnersApi } from '@/lib/api';
import { InteractionSheet } from '@/features/interactions/components/InteractionSheet';
import { TaskSheet } from '@/features/tasks/components/TaskSheet';
import { LeadSheet } from '@/features/leads/LeadForm';

export type SchnellWahl = 'notiz' | 'anruf' | 'aufgabe' | 'lead';

interface Bezug {
  customerId?: string;
  partnerId?: string;
  contactId?: string;
  /** Für den Satz unter dem Titel: „Zu Hausverwaltung Elbufer GmbH“. */
  name?: string;
}

/**
 * Der offene Datensatz als vorbelegter Bezug: Kunde, Lead (ein Kunde im
 * Lead-Status), Kontakt (mit seinem Kunden oder Partner) oder Partner. Die
 * Seiten haben ihn schon geladen, die Abfragen kommen aus dem Cache.
 */
function useBezug(): Bezug | null {
  const kunde = useMatch('/customers/:id')?.params.id;
  const lead = useMatch('/leads/:id')?.params.id;
  const kontakt = useMatch('/contacts/:id')?.params.id;
  const partner = useMatch('/partners/:id')?.params.id;
  const kundeId = kunde && kunde !== 'new' ? kunde : undefined;
  const kontaktId = kontakt && kontakt !== 'new' ? kontakt : undefined;
  const partnerId = partner && partner !== 'new' ? partner : undefined;

  const { data: k } = useQuery({ queryKey: ['customers', kundeId], queryFn: () => customersApi.get(kundeId!), enabled: !!kundeId });
  const { data: l } = useQuery({ queryKey: ['leads', lead], queryFn: () => customersApi.get(lead!), enabled: !!lead });
  const { data: c } = useQuery({ queryKey: ['contacts', kontaktId], queryFn: () => contactsApi.get(kontaktId!), enabled: !!kontaktId });
  const { data: p } = useQuery({ queryKey: ['partners', partnerId], queryFn: () => partnersApi.get(partnerId!), enabled: !!partnerId });

  if (kundeId) return { customerId: kundeId, name: k?.company_name };
  if (lead) return { customerId: lead, name: l?.company_name };
  if (kontaktId && c) {
    const person = `${c.first_name} ${c.last_name}`.trim();
    const firma = c.customer_name || c.partner_name;
    return {
      contactId: kontaktId,
      customerId: c.customer_id ?? undefined,
      partnerId: c.customer_id ? undefined : (c.partner_id ?? undefined),
      name: firma ? `${person} (${firma})` : person,
    };
  }
  if (partnerId) return { partnerId, name: p?.name };
  return null;
}

interface SchnellErfassenSheetsProps {
  /** Auswahl offen (das „+“ der Tab-Leiste). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wahl: SchnellWahl | null;
  onWahl: (wahl: SchnellWahl | null) => void;
}

/**
 * Schnell erfassen (Mobil M3): erst wählen, was erfasst wird, dann ein kurzes
 * Formular im Sheet. Auf einer Kunden-, Lead-, Kontakt- oder Partnerseite ist
 * der Bezug vorbelegt, sonst wählt man den Kunden über die Suche.
 */
export function SchnellErfassenSheets({ open, onOpenChange, wahl, onWahl }: SchnellErfassenSheetsProps) {
  const bezug = useBezug();
  const zu = bezug?.name ? `Zu ${bezug.name}` : undefined;
  // Notiz, Anruf und Aufgabe hängen am Kunden oder am Partner (TaskCreate.partner_id).
  const mitBezug = !!(bezug?.customerId || bezug?.partnerId);

  const optionen: SchnellErfassenOption[] = [
    { id: 'notiz', label: 'Notiz', hint: zu ?? 'Zu einem Kunden, Text genügt', icon: MessageSquare },
    { id: 'anruf', label: 'Anruf', hint: zu ?? 'Gesprächsnotiz zu einem Kunden', icon: Phone },
    { id: 'aufgabe', label: 'Aufgabe', hint: mitBezug && zu ? zu : 'Mit Fälligkeit, Kunde optional', icon: ClipboardList },
    { id: 'lead', label: 'Lead', hint: 'Firma, Ansprechpartner, Telefon', icon: UserPlus },
  ];

  const schliessen = (offen: boolean) => {
    if (!offen) onWahl(null);
  };

  return (
    <>
      <SchnellErfassen open={open} onOpenChange={onOpenChange} options={optionen} onWahl={(id) => onWahl(id as SchnellWahl)} />

      <InteractionSheet
        open={wahl === 'notiz'}
        onOpenChange={schliessen}
        title="Notiz"
        description={mitBezug ? zu : undefined}
        submitLabel="Notiz speichern"
        typ="note"
        kompakt
        customerId={bezug?.customerId}
        partnerId={bezug?.partnerId}
        contactId={bezug?.contactId}
      />
      <InteractionSheet
        open={wahl === 'anruf'}
        onOpenChange={schliessen}
        title="Anruf"
        description={mitBezug ? zu : undefined}
        submitLabel="Anruf speichern"
        typ="call"
        kompakt
        customerId={bezug?.customerId}
        partnerId={bezug?.partnerId}
        contactId={bezug?.contactId}
      />
      <TaskSheet
        open={wahl === 'aufgabe'}
        onOpenChange={schliessen}
        kompakt
        customerId={bezug?.customerId}
        partnerId={bezug?.partnerId}
        contactId={bezug?.contactId}
        description={mitBezug ? zu : undefined}
      />
      <LeadSheet open={wahl === 'lead'} onOpenChange={schliessen} kompakt />
    </>
  );
}
